import { UserProcessMode, UserProcessStatus, UserProcessStatusType } from '@interfaces';

export const CHECKLIST_DOC_KEY = 'CHECKLIST_SHARED';
export const ADDITIONAL_DOCUMENTS_KEY = 'ADDITIONAL_DOCUMENTS';

// Checklist generation backend error codes (returned in error.error.message)
export const FULL_PROCESS_GENERATION_IN_PROGRESS = 'FULL_PROCESS_GENERATION_IN_PROGRESS';
export const NO_CHECKLIST_DOCUMENT_FOR_PROCESS = 'NO_CHECKLIST_DOCUMENT_FOR_PROCESS';

// =================================================================================== APPLICATION DETAILS MAT-TABS CONFIG
export enum APP_DETAILS_TAB {
  'GENERAL' = 'GENERAL',
  'PRE_SURVEY' = 'PRE_SURVEY',
  'PERSONAL_DATA_FORM' = 'PERSONAL_DATA_FORM',
  'DOCS' = 'DOCS',
  'APPENDIX' = 'APPENDIX',
  'SUMMARY' = 'SUMMARY',
}

// Use this to construct correct queryParams in link referencing one of mat-tab inside management/application-details/...
export const APP_DETAILS_TAB_TO_INDEX: { [key: string]: number } = {
  [APP_DETAILS_TAB.GENERAL]: 0,
  [APP_DETAILS_TAB.PRE_SURVEY]: 1,
  [APP_DETAILS_TAB.PERSONAL_DATA_FORM]: 2,
  [APP_DETAILS_TAB.DOCS]: 3,
  [APP_DETAILS_TAB.APPENDIX]: 4,
  [APP_DETAILS_TAB.SUMMARY]: 5,
};

export const PRETTY_userProcessStatusCodes = {
  [UserProcessStatus.FILLING_PRE_SURVEY]: '1',
  [UserProcessStatus.PRE_SURVEY_VERIFICATION]: '2',
  [UserProcessStatus.FILLING_DETAILS]: '3',
  [UserProcessStatus.COMPLETING_DOCUMENTS]: '4',
  [UserProcessStatus.READY_FOR_REVIEW]: '5',
  [UserProcessStatus.CHANGES_REQUESTED]: '6',

  [UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE]: '7.1',
  [UserProcessStatus.SENDING_DOCS_TO_OFFICE_BY_POST]: '7.2',
  [UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST]: '7.3',
  [UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED]: '7.4',

  [UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS]: '8.1',
  [UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS]: '8.2',
  [UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS]: '8.3',

  // The two below are for L1 of MOS/POST modes
  [UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS]: '9.3',
  [UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS]: '9.4',

  // ONLY POST L3
  [UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF]: '9.1',

  // ONLY MOS L2 + L3 / POST L2
  [UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER]: '9.2',

  [UserProcessStatus.IMMIGROF_VISIT_SCHEDULED]: '10.1',
  [UserProcessStatus.IMMIGROF_VISIT_INFO_PROVIDED]: '10.2',

  [UserProcessStatus.IMMIGROF_VISIT_COMPLETED]: '11',
  [UserProcessStatus.DECISION_ISSUED]: '12',
  [UserProcessStatus.ARCHIVED]: '13',
};

export const USER_PROCESS_STATUS_FOR_MANAGERS = {
  [UserProcessStatus.FILLING_DETAILS]: UserProcessStatus.COMPLETING_DOCUMENTS,
  [UserProcessStatus.COMPLETING_DOCUMENTS]: UserProcessStatus.COMPLETING_DOCUMENTS,
  [UserProcessStatus.READY_FOR_REVIEW]: UserProcessStatus.COMPLETING_DOCUMENTS,
  [UserProcessStatus.CHANGES_REQUESTED]: UserProcessStatus.CHANGES_REQUESTED,

  [UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS]: [
    UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
  ],

  [UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE]: UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
  [UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST]: UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
  [UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED]: UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

  [UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS]: [
    UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,
  ],

  [UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS]: [UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS],
  [UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS]: [UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS],
  [UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS]: [UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS],

  [UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF]:
    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
  [UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER]:
    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

  [UserProcessStatus.IMMIGROF_VISIT_SCHEDULED]: UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
  [UserProcessStatus.IMMIGROF_VISIT_COMPLETED]: UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

  [UserProcessStatus.DECISION_ISSUED]: UserProcessStatus.DECISION_ISSUED,
  [UserProcessStatus.ARCHIVED]: UserProcessStatus.ARCHIVED,
};

export const statusesAllowedPerPath = {
  preSurvey: [UserProcessStatus.FILLING_PRE_SURVEY, UserProcessStatus.PRE_SURVEY_VERIFICATION],
  personalDetails: [
    // EDIT MODE
    UserProcessStatus.FILLING_DETAILS,
    UserProcessStatus.COMPLETING_DOCUMENTS,
    UserProcessStatus.CHANGES_REQUESTED,

    // READ ONLY MODE
    UserProcessStatus.READY_FOR_REVIEW,

    UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
    UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,

    UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
    UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
    UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

    UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
    UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
    UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,

    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

    UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
    UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

    UserProcessStatus.DECISION_ISSUED,
  ],
  documents: [
    // EDIT MODE
    UserProcessStatus.COMPLETING_DOCUMENTS,
    UserProcessStatus.CHANGES_REQUESTED,
    UserProcessStatus.READY_FOR_REVIEW,

    // READ ONLY MODE
    UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
    UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,

    UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
    UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
    UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

    UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
    UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
    UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,

    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

    UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
    UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

    UserProcessStatus.DECISION_ISSUED,
  ],
  summary: [
    UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
    UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,

    UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
    UserProcessStatus.SENDING_DOCS_TO_OFFICE_BY_POST,
    UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
    UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

    UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
    UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
    UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,

    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
    UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

    UserProcessStatus.IMMIGROF_VISIT_INFO_PROVIDED,
    UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
    UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

    UserProcessStatus.DECISION_ISSUED,
  ],
};

/**
 * List of statuses that guarantee the user has already completed confirmPersonalDetails at least once.
 * Be cautious when adding new statuses here, as this may impact application behavior between the survey and document steps.
 */
export const ALLOWED_TO_READ_WITH_STATUSES: UserProcessStatusType[] = [
  UserProcessStatus.READY_FOR_REVIEW,

  UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
  UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,

  UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
  UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
  UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

  UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
  UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
  UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,

  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

  UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

  UserProcessStatus.DECISION_ISSUED,
];

export const ALLOWED_TO_READ_ONLY_PERSONAL_DETAILS_WITH_STATUSES: UserProcessStatusType[] = [
  UserProcessStatus.READY_FOR_REVIEW,

  UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
  UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,

  UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
  UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
  UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

  UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
  UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
  UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,

  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

  UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

  UserProcessStatus.DECISION_ISSUED,
];

export const ALLOWED_TO_READ_ONLY_DOCUMENT_WITH_STATUSES: UserProcessStatusType[] = [
  UserProcessStatus.READY_FOR_REVIEW,

  UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
  UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,

  UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
  UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
  UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,

  UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
  UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
  UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,

  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,

  UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,

  UserProcessStatus.DECISION_ISSUED,
];

export const getAppDetailsTabNameByTabIndex = (index: number): APP_DETAILS_TAB => {
  const [tabName] = Object.entries(APP_DETAILS_TAB_TO_INDEX).find(([, tabIndex]) => {
    return tabIndex === index;
  });
  return tabName as APP_DETAILS_TAB;
};

export const MOS_LEVEL1_STATUSES = [
  UserProcessStatus.FILLING_PRE_SURVEY,
  UserProcessStatus.PRE_SURVEY_VERIFICATION,
  UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
  UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS,
  UserProcessStatus.ARCHIVED,
];

export const MOS_LEVEL2_STATUSES = [
  UserProcessStatus.FILLING_PRE_SURVEY,
  UserProcessStatus.PRE_SURVEY_VERIFICATION,
  UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,
  UserProcessStatus.IMMIGROF_VISIT_INFO_PROVIDED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,
  UserProcessStatus.DECISION_ISSUED,
  UserProcessStatus.ARCHIVED,
];

export const MOS_LEVEL3_STATUSES = [
  UserProcessStatus.FILLING_PRE_SURVEY,
  UserProcessStatus.PRE_SURVEY_VERIFICATION,
  UserProcessStatus.FILLING_DETAILS,
  UserProcessStatus.COMPLETING_DOCUMENTS,
  UserProcessStatus.READY_FOR_REVIEW,
  UserProcessStatus.CHANGES_REQUESTED,
  UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,
  UserProcessStatus.IMMIGROF_VISIT_INFO_PROVIDED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,
  UserProcessStatus.DECISION_ISSUED,
  UserProcessStatus.ARCHIVED,
];

export const POST_LEVEL1_STATUSES = [
  UserProcessStatus.FILLING_PRE_SURVEY,
  UserProcessStatus.PRE_SURVEY_VERIFICATION,
  UserProcessStatus.FILLING_DETAILS,
  UserProcessStatus.COMPLETING_DOCUMENTS,
  UserProcessStatus.READY_FOR_REVIEW,
  UserProcessStatus.CHANGES_REQUESTED,
  UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS,
  UserProcessStatus.ARCHIVED,
];

export const POST_LEVEL2_STATUSES = [
  UserProcessStatus.FILLING_PRE_SURVEY,
  UserProcessStatus.PRE_SURVEY_VERIFICATION,
  UserProcessStatus.FILLING_DETAILS,
  UserProcessStatus.COMPLETING_DOCUMENTS,
  UserProcessStatus.READY_FOR_REVIEW,
  UserProcessStatus.CHANGES_REQUESTED,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER,
  UserProcessStatus.IMMIGROF_VISIT_INFO_PROVIDED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,
  UserProcessStatus.DECISION_ISSUED,
  UserProcessStatus.ARCHIVED,
];

export const POST_LEVEL3_STATUSES = [
  UserProcessStatus.FILLING_PRE_SURVEY,
  UserProcessStatus.PRE_SURVEY_VERIFICATION,
  UserProcessStatus.FILLING_DETAILS,
  UserProcessStatus.COMPLETING_DOCUMENTS,
  UserProcessStatus.READY_FOR_REVIEW,
  UserProcessStatus.CHANGES_REQUESTED,
  UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE,
  UserProcessStatus.SENDING_DOCS_TO_OFFICE_BY_POST,
  UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST,
  UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED,
  UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS,
  UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF,
  UserProcessStatus.IMMIGROF_VISIT_SCHEDULED,
  UserProcessStatus.IMMIGROF_VISIT_COMPLETED,
  UserProcessStatus.DECISION_ISSUED,
  UserProcessStatus.ARCHIVED,
];

/**
 * Party expected to act while a user-process sits in a given status.
 * Used by the application-status-modal to show consultants who is responsible for the next step.
 */
export type StatusResponsibleParty = 'FOREIGNER' | 'CONSULTANT' | 'SYSTEM';

export const STATUS_RESPONSIBLE_PARTY: Record<string, StatusResponsibleParty> = {
  [UserProcessStatus.FILLING_PRE_SURVEY]: 'FOREIGNER',
  [UserProcessStatus.PRE_SURVEY_VERIFICATION]: 'CONSULTANT',
  [UserProcessStatus.FILLING_DETAILS]: 'FOREIGNER',
  [UserProcessStatus.COMPLETING_DOCUMENTS]: 'FOREIGNER',
  [UserProcessStatus.READY_FOR_REVIEW]: 'CONSULTANT',
  [UserProcessStatus.CHANGES_REQUESTED]: 'FOREIGNER',

  [UserProcessStatus.REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS]: 'FOREIGNER',
  [UserProcessStatus.REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS]: 'FOREIGNER',

  [UserProcessStatus.SELECTING_DOCUMENTS_SIGN_MODE]: 'FOREIGNER',
  [UserProcessStatus.SENDING_DOCS_TO_OFFICE_BY_POST]: 'FOREIGNER',
  [UserProcessStatus.DOCUMENTS_SENT_TO_OFFICE_BY_POST]: 'CONSULTANT',
  [UserProcessStatus.SIGNED_DOCUMENTS_RECEIVED]: 'CONSULTANT',

  [UserProcessStatus.APPOINTMENT_TO_SIGN_DOCS]: 'FOREIGNER',
  [UserProcessStatus.APPOINTMENT_TO_VERIFY_DOCS]: 'FOREIGNER',
  [UserProcessStatus.APPOINTMENT_TO_SUBMIT_TO_MOS]: 'FOREIGNER',

  [UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF]: 'CONSULTANT',
  [UserProcessStatus.AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER]: 'FOREIGNER',

  [UserProcessStatus.IMMIGROF_VISIT_SCHEDULED]: 'CONSULTANT',
  [UserProcessStatus.IMMIGROF_VISIT_INFO_PROVIDED]: 'CONSULTANT',

  [UserProcessStatus.IMMIGROF_VISIT_COMPLETED]: 'CONSULTANT',
  [UserProcessStatus.DECISION_ISSUED]: 'CONSULTANT',
  [UserProcessStatus.ARCHIVED]: 'SYSTEM',
};

export const getStatusesAllowedInUserProcess = ({
  hasAssistantService,
  hasFullHandling,
  mode,
  processId,
}: {
  mode: UserProcessMode;
  hasAssistantService: boolean;
  hasFullHandling: boolean;
  processId: string | null;
}): UserProcessStatusType[] => {
  if (!processId) {
    return [UserProcessStatus.FILLING_PRE_SURVEY, UserProcessStatus.PRE_SURVEY_VERIFICATION];
  }

  if (!hasFullHandling && !hasAssistantService) {
    return mode === UserProcessMode.MOS ? MOS_LEVEL1_STATUSES : POST_LEVEL1_STATUSES;
  }
  if (hasAssistantService && !hasFullHandling) {
    return mode === UserProcessMode.MOS ? MOS_LEVEL2_STATUSES : POST_LEVEL2_STATUSES;
  }
  if (hasFullHandling) {
    return mode === UserProcessMode.MOS ? MOS_LEVEL3_STATUSES : POST_LEVEL3_STATUSES;
  }

  // fallback, should never happen
  console.error('getStatusesAllowedInUserProcess: unexpected combination of hasFullHandling and hasAssistantService');
  return MOS_LEVEL1_STATUSES;
};
